// useTravelItems.js
// This is a custom React hook
// A hook is just a function that starts with "use" and manages state/side effects
// We pull all the Supabase logic in here so travel.js stays clean and readable
// This follows the exact same pattern as your existing useGigs hook

import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
// supabase = your already-configured Supabase client
// adjust this import path if yours is in a different location

export function useTravelItems() {

  // ---- STATE ----

  const [travelItems, setTravelItems] = useState([])
  // travelItems = the array of items fetched from Supabase
  // starts as empty array, gets filled in by fetchItems below

  const [loading, setLoading] = useState(true)
  // loading = true while we're waiting for Supabase to respond
  // travel.js uses this to show "Loading..." instead of an empty list


  // ---- FETCH ON MOUNT ----

  useEffect(() => {
    fetchItems()
  }, [])
  // useEffect with [] = runs once when the component first mounts
  // [] = empty dependency array = "only run this once, not on every render"
  // this is how you load data when a page opens


  // ---- FETCH ALL ITEMS ----

  const fetchItems = async () => {
    // async = this function uses await (handles promises)

    try {
      setLoading(true)
      // set loading to true before the request starts
      // travel.js will show "Loading..." while this runs

      const { data: { user } } = await supabase.auth.getUser()
      // get the currently logged in user
      // we need their id to only fetch THEIR items

      const { data, error } = await supabase
        .from('travel_items')
        // which Supabase table to query
        .select('*')
        // select all columns
        .eq('user_id', user.id)
        // .eq = "equal to" - only get rows where user_id matches this user
        // this is how each user only sees their own checklist
        .order('created_at', { ascending: true })
        // show oldest items first (the order they were added)

      if (error) throw error
      // if Supabase returned an error, throw it to the catch block

      setTravelItems(data)
      // update state with the fetched items
      // React re-renders travel.js with the real data

    } catch (error) {
      console.error('Error fetching travel items:', error.message)
    } finally {
      setLoading(false)
      // finally = runs whether the request succeeded or failed
      // always turn off loading when done
    }
  }


  // ---- ADD ITEM ----

  const addItem = async (name) => {
    // name = the string the user typed, passed in from travel.js

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { data, error } = await supabase
        .from('travel_items')
        .insert([{
          name,
          // name: name shorthand - same as writing name: name
          completed: false,
          // new items always start as not packed
          user_id: user.id
          // attach this item to the logged in user
        }])
        .select()
        // .select() returns the newly created row so we can add it to state

      if (error) throw error

      setTravelItems(prev => [...prev, data[0]])
      // prev = the current travelItems array
      // [...prev, data[0]] = copy the array and add the new item at the end
      // data[0] = the first (and only) item Supabase returned
      // this updates the UI instantly without needing to re-fetch everything

    } catch (error) {
      console.error('Error adding travel item:', error.message)
    }
  }


  // ---- TOGGLE COMPLETED ----

  const toggleCompleted = async (id) => {
    // id = the item's uuid from Supabase

    const item = travelItems.find(i => i.id === id)
    // find the item in our local state so we know its current completed value

    try {
      const { error } = await supabase
        .from('travel_items')
        .update({ completed: !item.completed })
        // !item.completed flips true to false and false to true
        .eq('id', id)
        // only update the row with this specific id

      if (error) throw error

      setTravelItems(prev => prev.map(i =>
        i.id === id ? { ...i, completed: !i.completed } : i
      ))
      // update local state to match what we just saved to Supabase
      // .map() returns a new array - never mutate state directly
      // { ...i, completed: !i.completed } = copy item but flip completed
      // : i = all other items stay exactly the same

    } catch (error) {
      console.error('Error toggling item:', error.message)
    }
  }


  // ---- DELETE ITEM ----

  const deleteItem = async (id) => {

    try {
      const { error } = await supabase
        .from('travel_items')
        .delete()
        .eq('id', id)
        // delete the row from Supabase where id matches

      if (error) throw error

      setTravelItems(prev => prev.filter(i => i.id !== id))
      // remove it from local state too
      // .filter() keeps every item EXCEPT the deleted one
      // React re-renders the list without it

    } catch (error) {
      console.error('Error deleting item:', error.message)
    }
  }


  // ---- UNCHECK ALL ----

  const uncheckAll = async () => {
    // Sets completed = false on every item for this user
    // Called by travel.js when the user confirms "Reset all"

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { error } = await supabase
        .from('travel_items')
        .update({ completed: false })
        // set every row back to not-packed
        .eq('user_id', user.id)
        // only touch THIS user's items -- never affect other users' data

      if (error) throw error

      setTravelItems(prev => prev.map(i => ({ ...i, completed: false })))
      // mirror the DB change in local state so the UI updates instantly
      // .map() returns a new array with completed flipped to false on every item
      // React sees a new array reference and re-renders the list

    } catch (error) {
      console.error('Error unchecking all items:', error.message)
      throw error
      // re-throw so travel.js can catch it and show the alert to the user
      // if we swallow the error here, travel.js never knows it failed
    }
  }


  // ---- DELETE ALL ITEMS ----

  const deleteAllItems = async () => {
    // Deletes every travel item for this user from Supabase
    // Called by travel.js when the user confirms "Delete All"

    try {
      const { data: { user } } = await supabase.auth.getUser()

      const { error } = await supabase
        .from('travel_items')
        .delete()
        .eq('user_id', user.id)
        // delete all rows belonging to this user
        // .eq('user_id') ensures we never touch another user's rows

      if (error) throw error

      setTravelItems([])
      // clear local state -- the list is now empty
      // passing [] tells React to re-render with no items

    } catch (error) {
      console.error('Error deleting all items:', error.message)
      throw error
      // re-throw so travel.js can catch it and show the alert
    }
  }


  // ---- RETURN ----

  return {
    travelItems,
    loading,
    addItem,
    toggleCompleted,
    deleteItem,
    uncheckAll,      // resets all items to completed = false
    deleteAllItems,  // removes all items from DB and clears state
  }
  // return everything that travel.js needs
  // this is what gets destructured when travel.js calls useTravelItems()
}